/**
 * @file
 * Drupal.berUI adapter core: prototype extensions, composition helpers,
 * the registry and the ber-ui:build extension point.
 */

((Drupal) => {
  const { components, tooltip, dom } = Drupal.berTags;
  const { Component, Container, events } = window.BerUi;
  const { LOADING, LOADED } = events;

  // Drupal-side extensions of the core classes: the shared tooltip bubble.
  // opts.align: start/end/center/auto - auto (default) is the button mode,
  // the side caret pointed at the target's center.
  Component.prototype.withTooltip = function withTooltip(lines, opts = {}) {
    const build =
      typeof lines === 'function'
        ? lines
        : () =>
            Array.isArray(lines) ? lines : [['ber-tip__text', String(lines)]];
    const align = opts.align && opts.align !== 'auto' ? opts.align : 'button';
    const show = () => tooltip.open(this.el, build(this.el), { align });
    // mouseover/mouseout with the contains(relatedTarget) guard, exactly
    // like the delegated attachers: the browser re-syncs hover with
    // trusted out/over pairs whose relatedTarget IS the element whenever
    // the DOM changes under an open bubble - bare enter/leave treat each
    // pair as a real exit and the bubble flickers in a feedback loop.
    this.el.addEventListener('mouseover', (event) => {
      if (!this.el.contains(event.relatedTarget)) {
        show();
      }
    });
    this.el.addEventListener('mouseout', (event) => {
      if (!this.el.contains(event.relatedTarget)) {
        tooltip.hide();
      }
    });
    // A press retires the bubble until the pointer re-enters. Without it,
    // buttons that open something on click (a confirm:, a popover) fight
    // the focus-driven re-show and the bubble flickers.
    this.el.addEventListener('pointerdown', () => tooltip.hide());
    // Keyboard focus only: click focus - and the programmatic refocus
    // when a confirm closes - must not pop the bubble back up.
    this.el.addEventListener('focusin', () => {
      if (this.el.matches(':focus-visible')) {
        show();
      }
    });
    this.el.addEventListener('focusout', () => tooltip.hide());
    return this;
  };

  // Every component is a core Component; every region and container is a
  // core Container - so add()/addComponent()/addAll() exist uniformly.
  const base = (root) => new Component(root);
  const region = (target) => (target ? new Container(target) : null);

  const ui = {};

  // Container behavior mixed into windows and views: region getters, the
  // component mounter and named child views (getView/addView).
  const container = (api, { header, body, footer }) => {
    const regions = {
      header: region(header),
      body: region(body),
      footer: region(footer),
    };
    const views = new Map();
    return Object.assign(api, {
      getHeader: () => regions.header,
      getBody: () => regions.body,
      getFooter: () => regions.footer,
      addComponent(child, slot = 'body') {
        (regions[slot] || regions.body).addComponent(child);
        return api;
      },
      add(child, slot = 'body') {
        return api.addComponent(child, slot);
      },
      // opts.hides: components (or elements) hidden while this view is the
      // active one - e.g. a full-screen "add" view hiding the window header.
      // showView() restores them when a view without them takes over.
      addView(name, view, { hides = [] } = {}) {
        views.set(name, { view, hides });
        body.appendChild(view.el);
        return api;
      },
      getView: (name) => (views.get(name) || {}).view,
      showView(name) {
        const active = views.get(name);
        if (!active) {
          return api;
        }
        views.forEach((entry, key) =>
          entry.view[key === name ? 'show' : 'hide'](),
        );
        const targets = new Set();
        views.forEach((entry) =>
          entry.hides.forEach((target) => targets.add(target)),
        );
        targets.forEach((target) => {
          const off = active.hides.includes(target);
          if (typeof target.show === 'function') {
            target[off ? 'hide' : 'show']();
          } else {
            target.hidden = off;
          }
        });
        return api;
      },
    });
  };

  // Header components.
  ui.search = (options) => {
    const api = base(components.searchBox(options));
    ui.emit('search', { component: api });
    return api;
  };

  // The toolbar implementation lives with the components; wrap it so a built
  // toolbar announces itself too.
  ui.toolbar = (options) => {
    const api = components.toolbar(options);
    ui.emit('toolbar', { component: api });
    return api;
  };

  ui.events = { LOADING, LOADED };
  // Composition internals the per-component files build with.
  ui.compose = { base, region, container };
  ui.Component = Component;
  ui.Container = Container;

  const registry = new Map();
  ui.register = (name, factory) => {
    registry.set(name, factory);
    return ui;
  };
  ui.create = (name, options = {}) => {
    const factory = registry.get(name);
    if (!factory) {
      throw new Error(`berUI: unknown component "${name}"`);
    }
    return factory(options);
  };
  ui.register('search', ui.search);
  ui.register('toolbar', ui.toolbar);

  ui.extend = (root, scope, detail = {}) => {
    root.dispatchEvent(
      new CustomEvent('ber-ui:build', {
        bubbles: true,
        detail: { scope, ...detail },
      }),
    );
    return ui;
  };

  // Announce a freshly built component so extenders can hook it by listening
  // for `ber-ui:build:<type>` - the event only fires for that type, so the
  // listener needs no checks. Fired on document because components are not
  // attached to the page yet when they build. detail carries the component
  // instance (with its region getters / add methods); named views pass their
  // owning window too, so `ber-ui:build:view:browse` can reach the footer.
  // The full list of events is the README "JS events" table.
  ui.emit = (type, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent(`ber-ui:build:${type}`, {
        detail: { type, ...detail },
      }),
    );
    return ui;
  };

  Drupal.berUI = ui;
})(Drupal, once);
